import React, { useState } from "react";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Phone,
  Siren,
  Sparkles,
  Stethoscope,
  Undo2,
  User2,
} from "lucide-react";
import FollowUpFlow from "@/components/app/FollowUpFlow";
import FirstTimeFlow from "@/components/app/FirstTimeFlow";
import RefundFlow from "@/components/app/RefundFlow";

export default function PatientFlow({ kiosk }) {
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [patient, setPatient] = useState({ name: "", age: "", phone: "", location: "" });
  const [consult, setConsult] = useState(null);
  const [emergencyOpen, setEmergencyOpen] = useState(false);

  const reset = () => {
    setStep(0);
    setPatient({ name: "", age: "", phone: "", location: "" });
    setConsult(null);
  };

  // Step 1: Emergency Disclaimer
  if (step === 0) {
    return (
      <div className="grain-bg mx-auto max-w-3xl py-6">
        <Card className="pulse-emergency border-2 border-red-500 bg-red-50 p-6 sm:p-8" data-testid="emergency-banner">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-red-600 text-white">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-red-700">
                {t("emergency_title")}
              </div>
              <h2 className="mb-3 text-xl font-bold text-red-900 sm:text-2xl">{t("emergency_msg")}</h2>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={() => setStep(1)}
              data-testid="emergency-proceed-button"
              className="h-14 flex-1 bg-sky-600 text-base font-semibold hover:bg-sky-700 sm:text-lg"
            >
              {t("proceed_non_emergency")} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => setEmergencyOpen(true)}
              data-testid="emergency-help-button"
              className="h-14 flex-1 bg-red-600 text-base font-semibold hover:bg-red-700 sm:text-lg"
            >
              <Siren className="mr-2 h-5 w-5" /> {t("emergency_help")}
            </Button>
          </div>
        </Card>

        <Dialog open={emergencyOpen} onOpenChange={setEmergencyOpen}>
          <DialogContent className="border-red-300 bg-white" data-testid="emergency-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Siren className="h-5 w-5" /> Emergency Response Activated
              </DialogTitle>
            </DialogHeader>
            <p className="text-slate-700">{t("emergency_called")}</p>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-red-800">
                <Phone className="h-5 w-5" />
                <span className="font-semibold">Emergency Hotline: 108</span>
              </div>
            </div>
            <Button onClick={() => setEmergencyOpen(false)}>Close</Button>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Step 2: Patient info
  if (step === 1) {
    const validPhone = /^[0-9]{10}$/.test(patient.phone);
    const valid = patient.name.trim() && patient.age && validPhone && patient.location.trim();
    return (
      <div className="mx-auto max-w-2xl py-6">
        <StepNav step={1} />
        <Card className="border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="mb-1 text-2xl font-bold tracking-tight sm:text-3xl">{t("patient_info_title")}</h2>
          <p className="mb-6 text-sm text-slate-500">{t("patient_info_subtitle")}</p>
          <div className="grid gap-5">
            <Field label={t("full_name")} testId="input-name">
              <Input value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} placeholder="Rahul Verma" data-testid="patient-name-input" />
            </Field>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label={t("age")} testId="input-age">
                <Input type="number" min="0" max="120" value={patient.age} onChange={(e) => setPatient({ ...patient, age: e.target.value })} placeholder="32" data-testid="patient-age-input" />
              </Field>
              <Field label={t("phone")} testId="input-phone">
                <Input value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" data-testid="patient-phone-input" />
              </Field>
            </div>
            <Field label={t("location")} testId="input-location">
              <Input value={patient.location} onChange={(e) => setPatient({ ...patient, location: e.target.value })} placeholder="Bengaluru" data-testid="patient-location-input" />
            </Field>
          </div>
          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStep(0)} data-testid="back-btn">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
            </Button>
            <Button
              disabled={!valid}
              onClick={() => setStep(2)}
              data-testid="patient-info-next-btn"
              className="h-12 bg-sky-600 px-6 hover:bg-sky-700 disabled:opacity-50"
            >
              {t("next_step")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Step 3: Consultation type
  if (step === 2) {
    const cards = [
      { id: "first_time", icon: Sparkles, title: t("first_time"), desc: t("first_time_desc"), color: "from-sky-500 to-teal-500" },
      { id: "follow_up", icon: Stethoscope, title: t("follow_up"), desc: t("follow_up_desc"), color: "from-indigo-500 to-sky-500" },
      { id: "transfer", icon: Undo2, title: t("refund_transfer"), desc: t("refund_transfer_desc"), color: "from-amber-500 to-orange-500" },
    ];
    return (
      <div className="mx-auto max-w-5xl py-6">
        <StepNav step={2} />
        <h2 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">{t("consultation_title")}</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {cards.map(({ id, icon: Icon, title, desc, color }) => (
            <button
              key={id}
              onClick={() => { setConsult(id); setStep(3); }}
              data-testid={`consultation-${id}-card`}
              className="group text-left transition-transform hover:-translate-y-1"
            >
              <Card className="relative h-full overflow-hidden border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-xl">
                <div className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${color} text-white shadow-md`}>
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{title}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{desc}</p>
                <ArrowRight className="absolute bottom-5 right-5 h-5 w-5 text-slate-300 transition-colors group-hover:text-sky-500" />
              </Card>
            </button>
          ))}
        </div>
        <div className="mt-6">
          <Button variant="ghost" onClick={() => setStep(1)} data-testid="back-btn">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
          </Button>
        </div>
      </div>
    );
  }

  // Step 4: Branch flows
  if (consult === "first_time") return <FirstTimeFlow patient={patient} onReset={reset} onBack={() => setStep(2)} />;
  if (consult === "follow_up") return <FollowUpFlow patient={patient} onReset={reset} onBack={() => setStep(2)} />;
  if (consult === "transfer") return <RefundFlow patient={patient} onReset={reset} onBack={() => setStep(2)} />;

  return null;
}

function StepNav({ step }) {
  const steps = ["Emergency", "Info", "Type", "Book"];
  return (
    <div className="mb-6 flex items-center gap-2 text-xs text-slate-500" data-testid="step-nav">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-1.5 ${i <= step ? "text-sky-700" : ""}`}>
            <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
              i < step ? "bg-emerald-500 text-white" : i === step ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"
            }`}>
              {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className="hidden font-medium sm:block">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div className="grid gap-1.5" data-testid={testId}>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
